import { useEffect } from "react";
import { type ProductMedia } from "@/lib/supabase";

type Props = {
  open: boolean;
  media: ProductMedia[];
  index: number;
  alt: string;
  onClose: () => void;
  onChange: (i: number) => void;
};

export default function Lightbox({ open, media, index, alt, onClose, onChange }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onChange((index + 1) % media.length);
      if (e.key === "ArrowLeft") onChange((index - 1 + media.length) % media.length);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, index, media.length, onChange, onClose]);

  if (!open || media.length === 0) return null;

  const current = media[index];
  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-foreground-950/95 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close gallery"
        className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 cursor-pointer transition-colors"
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      <span className="absolute top-6 left-1/2 -translate-x-1/2 text-background-50/70 text-xs tracking-[0.3em] uppercase">
        {String(index + 1).padStart(2, "0")} / {String(media.length).padStart(2, "0")}
      </span>

      {media.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => onChange((index - 1 + media.length) % media.length)}
            aria-label="Previous image"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/25 text-background-50 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            type="button"
            onClick={() => onChange((index + 1) % media.length)}
            aria-label="Next image"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/25 text-background-50 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </>
      )}

      <div className="max-w-[90vw] max-h-[85vh] flex items-center justify-center px-4">
        <img
          key={current.url}
          src={current.url}
          alt={`${alt} — full view ${index + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded-lg animate-zoomIn"
        />
      </div>
    </div>
  );
}